import React from "react";
import { useIntl } from "react-intl";

import { ColumnProps } from "src/common/components/DataTable";
import { MobileColumnProps } from "src/common/components/MobileTable";
import { SocialSecurityNumber } from "src/common/components/SocialSecurityNumber";
import { StatusBadge } from "src/common/components/StatusBadge";
import { messages } from "src/modules/basic-health/patient-list/messages";
import { PatientStatus } from "src/modules/basic-health/patient-list/types";
import {
	compareNumberEvents,
	mapNumberEventToString,
	renderNumberEvent
} from "src/modules/shared/patient-list";

export function useDesktopColumnProps(): ColumnProps[] {
	const status = useStatusColumnDesktop();
	const socialSecurityNumber = useSocialSecurityNumberColumn();
	const name = useNameColumn();
	const age = useAgeColumn();
	const sex = useSexColumn();
	const city = useCityColumn();
	const weight = useWeightColumn();
	const bloodGroup = useBloodGroupColumn();

	return React.useMemo(
		() => [
			status,
			socialSecurityNumber,
			name,
			age,
			sex,
			city,
			weight,
			bloodGroup
		],
		[status, socialSecurityNumber, name, age, sex, city, weight, bloodGroup]
	);
}

export function useMobileColumnProps(): MobileColumnProps {
	const status = useStatusColumnMobile();
	const name = useNameColumn();
	const socialSecurityNumber = useSocialSecurityNumberColumn();

	const bloodGroup = useBloodGroupColumn();
	const age = useAgeColumn();
	const weight = useWeightColumn();

	return React.useMemo(
		() => ({
			icon: status,
			title: name,
			subtitle: socialSecurityNumber,
			fix: [bloodGroup, age, weight],
			optional: []
		}),
		[status, name, socialSecurityNumber, bloodGroup, age, weight]
	);
}

function useStatusColumnDesktop(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "status",
			label: formatMessage(messages.status),
			render({ value, longLabel }: PatientStatus): React.ReactNode {
				return <StatusBadge value={value} label={longLabel} />;
			},
			mapToString({ longLabel }: PatientStatus): string {
				return longLabel;
			}
		}),
		[formatMessage]
	);
}

function useStatusColumnMobile(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "status",
			label: formatMessage(messages.status),
			render({ value, shortLabel }: PatientStatus): React.ReactNode {
				return <StatusBadge value={value} label={shortLabel} />;
			},
			mapToString({ shortLabel }: PatientStatus): string {
				return shortLabel;
			}
		}),
		[formatMessage]
	);
}

function useNameColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "name",
			label: formatMessage(messages.name)
		}),
		[formatMessage]
	);
}

function useSocialSecurityNumberColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "socialSecurityNumber",
			label: formatMessage(messages.socialSecurityNumber),
			render(value: string): React.ReactNode {
				return <SocialSecurityNumber>{value}</SocialSecurityNumber>;
			}
		}),
		[formatMessage]
	);
}

function useAgeColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "age",
			label: formatMessage(messages.ageLabel),
			render(value: number): React.ReactNode {
				return (
					<>
						<b>{value}</b> <i>{formatMessage(messages.ageUnit)}</i>
					</>
				);
			},
			mapToString(value: number): string {
				return `${value} ${formatMessage(messages.ageUnit)}`;
			},
			compare(a: number, b: number): number {
				if (a < b) {
					return -1;
				}
				if (a > b) {
					return 1;
				}
				return 0;
			}
		}),
		[formatMessage]
	);
}

function useSexColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({ key: "sex", label: formatMessage(messages.sex) }),
		[formatMessage]
	);
}

function useCityColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({ key: "city", label: formatMessage(messages.city) }),
		[formatMessage]
	);
}

function useWeightColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "weight",
			label: formatMessage(messages.weight),
			render: renderNumberEvent,
			mapToString: mapNumberEventToString,
			compare: compareNumberEvents
		}),
		[formatMessage]
	);
}

function useBloodGroupColumn(): ColumnProps {
	const { formatMessage } = useIntl();
	return React.useMemo(
		() => ({
			key: "bloodGroup",
			label: formatMessage(messages.bloodGroup)
		}),
		[formatMessage]
	);
}
